import { BillingCountry } from '@enums/billing-countries';
import { Gender } from '@enums/gender';
import { Sites } from '@enums/sites';
import { ApiOrderRequest } from '@interfaces/api-order-request';
import { InHomeCollection } from '@interfaces/in-home-collection';
import {
  BitPayPayment,
  CreditCardPayment,
  ExternalPayment,
  FreePayment,
  GiftCardPayment,
  Payment,
  RechargePayment,
} from '@medology/ngx-payment';

import { AnalyteCareCouponDto } from './analyte-care-coupon-dto';
import { ExtraCharge } from './extra-charge';

export class OrderRequest implements ApiOrderRequest {
  public readonly orderingEndpoint: string = '/api/v1/order';

  public payment_type: 'credit-card' | 'cryptocurrencies' | 'braintree' | 'payLater' | 'recharge' | 'giftCard' | 'free';

  // Optional parameters
  public address?: string;
  public address2?: string;
  public billing_country?: BillingCountry;
  public billing_zip_code?: string;
  public bitpay?: BitPayPayment;
  public card_expiration?: string;
  public card_number?: string;
  public charge_amount?: number;
  public city?: string;
  public cvv?: string;
  public deviceData?: string;
  public email?: string;
  public extra_charge?: ExtraCharge;
  public gift_card_code?: string;
  public guardian_first_name?: string;
  public guardian_last_name?: string;
  public guardian_relationship?: string;
  public in_home_collection?: InHomeCollection;
  public leave_message?: 'YES' | 'NO';
  public nonce?: string;
  public note?: string;
  public partner_email?: string;
  public phone?: string;
  public state?: string;
  public plan_id?: string | number;
  public recurly?: boolean;
  public upsells?: number[];
  public zip?: string;

  public constructor(
    public accountType: 'email' | 'phone' | 'none',
    public coupon: string | AnalyteCareCouponDto[] | null,
    public cjevent: string | null,
    public dob: string,
    public first_name: string,
    public gender: Gender,
    public lab_id: number,
    public last_name: string,
    public site_name: Sites,
    public tests: number[] | { test_id: string; reference_id: string }[],
    public user_id: number | null,
    payment: Payment,
  ) {
    this.parsePaymentMethod(payment);
  }

  /**
   * Flags the order to be billed through the external subscription service, including the gift card in the same
   * request when one accompanies the primary payment. The billing service resolves the amount covered by each payment
   * method, so no charge amount is sent.
   *
   * @param {GiftCardPayment} giftCardPayment the gift card used alongside the primary payment, if any
   */
  public markAsExternalSubscription(giftCardPayment?: GiftCardPayment): void {
    this.recurly = true;

    if (giftCardPayment) {
      this.gift_card_code = giftCardPayment.code;
    }
  }

  /**
   * Save guardian information to be sent as request parameters, also rendering a note.
   */
  public parseGuardianInfo(patient: any): void {
    if (patient.parentGuardian.relationToChild && patient.parentGuardian.firstName) {
      this.guardian_first_name = patient.parentGuardian.firstName;
      this.guardian_last_name = patient.parentGuardian.lastName;
      this.guardian_relationship = patient.parentGuardian.relationToChild;
      this.note =
        `This patient is minor. The patient's ${patient.parentGuardian.relationToChild} ` +
        `(${patient.parentGuardian.firstName} ${patient.parentGuardian.lastName}) ` +
        'approved and paid for the testing.';
    }
  }

  /**
   * Extracts the payment information.
   *
   * @param {Payment} payment the payment information from the package
   */
  private parsePaymentMethod(payment: Payment): void {
    switch (true) {
      case payment instanceof CreditCardPayment:
        this.parseCreditCardPayment(payment);

        break;
      case payment instanceof ExternalPayment:
        this.payment_type = 'braintree';
        this.nonce = payment.nonce;

        break;
      case payment instanceof RechargePayment:
        this.payment_type = 'recharge';

        break;
      case payment instanceof GiftCardPayment:
        this.payment_type = 'giftCard';
        this.gift_card_code = payment.code;

        break;
      case payment instanceof FreePayment:
        this.payment_type = 'free';

        break;
      default:
        this.payment_type = 'payLater';
    }
  }

  /**
   * Extracts the credit card payment information.
   *
   * @param {CreditCardPayment} payment the credit card payment information
   */
  private parseCreditCardPayment(payment: CreditCardPayment): void {
    this.card_number = payment.cardNumber;
    this.card_expiration = String(payment.expirationMonth).padStart(2, '0') + String(payment.expirationYear).slice(-2);
    this.payment_type = 'credit-card';
    this.billing_country = <BillingCountry>payment.billingCountry;
    this.billing_zip_code = payment.billingZipCode;
    this.cvv = payment.cardCvv;
  }
}
